import { Formik } from 'formik';
import * as Yup from 'yup';

import { saveProfile } from '../../store/profileSlice';
import type { UpdateProfileRequest } from '../../types/profile.types';

import { Box } from '@components/Box';
import {
  FormButtonsContainer,
  FormSectionContainer,
} from '@components/Container';
import { CancelButton, FormikInput } from '@components/Form';
import { SubmitButton } from '@components/Form/SubmitButton';
import { useNotification } from '@hooks/useNotification';
import { useAppDispatch, useAppSelector } from '@store/hooks';

interface Props {
  onCancel: () => void;
}

const validationSchema = Yup.object({
  firstName: Yup.string().required('First name is required').max(100),
  middleName: Yup.string().max(100).optional(),
  lastName: Yup.string().required('Last name is required').max(100),
});

const ProfileEditForm: React.FC<Props> = ({ onCancel }) => {
  const dispatch = useAppDispatch();
  const { profile, isSaving } = useAppSelector((s) => s.settingsProfile);
  const { showSuccess, showError } = useNotification();

  if (!profile) return null;

  const initialValues: UpdateProfileRequest = {
    firstName: profile.firstName,
    middleName: profile.middleName ?? '',
    lastName: profile.lastName,
    phoneNumber: profile.phoneNumber ?? '',
  };

  return (
    <Box size="medium">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnBlur
        validateOnChange
        onSubmit={(values, actions) => {
          dispatch(saveProfile(values))
            .unwrap()
            .then(() => {
              showSuccess('Profile updated successfully.');
              onCancel();
            })
            .catch((err: string) => {
              showError(err);
            })
            .finally(() => {
              actions.setSubmitting(false);
            });
        }}
      >
        {(formikProps) => (
          <form onSubmit={formikProps.handleSubmit}>
            <FormSectionContainer>
              <FormikInput
                label="First Name"
                name="firstName"
                autoComplete="given-name"
              />
              <FormikInput
                label="Middle Name"
                name="middleName"
                autoComplete="additional-name"
              />
              <FormikInput
                label="Last Name"
                name="lastName"
                autoComplete="family-name"
              />
            </FormSectionContainer>
            <FormButtonsContainer>
              <CancelButton onClick={onCancel} />
              <SubmitButton
                label="Save"
                disabled={isSaving || formikProps.isSubmitting}
              />
            </FormButtonsContainer>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default ProfileEditForm;
